<template>
  <div class="usermedia-connector" v-if="enableCasterCommunication && streamDocId && localCameraStream">
    <template v-if="isJanus">
      <JanusCasterCommunication
        v-if="selectedWrtcServer"
        :streamToPlay="streamDocId"
        :onAir="isCasting"
        :localStream="localCameraStream"
        :videoBitrate="requiredVideoBitrate"
        :audioBitrate="requiredAudioBitrate"
        :wrtcServer="selectedWrtcServer"
        :videoCodec="videoCodec"
        :udpEnabled="useUdp"
        :enableBandwidthCheck="enableBandwidthCheck"
        @onNetworkStats="onUpdateNetworkStats"
        @wsConnectionClosed="wsConnectionClosed"
      />
      <JanusCasterCommunication
        v-if="makeLowrateVariant && selectedWrtcServer"
        :streamToPlay="streamDocId + '_lowratecaster'"
        :onAir="isCasting"
        :localStream="localCameraStream"
        :videoBitrate="lowrateVideoBitrate"
        :audioBitrate="lowrateAudioBitrate"
        :wrtcServer="selectedWrtcServer"
        :videoCodec="videoCodec"
        :udpEnabled="useUdp"
        @wsConnectionClosed="wsConnectionClosed"
      />
      <JanusCasterCommunication
        v-if="enableStationChinaProxy && chinaWrtcServer"
        :streamToPlay="streamDocId"
        :onAir="isCasting"
        :localStream="localCameraStream"
        :videoBitrate="requiredVideoBitrate"
        :audioBitrate="requiredAudioBitrate"
        :wrtcServer="chinaWrtcServer"
        :videoCodec="videoCodec"
        :udpEnabled="useUdp"
        @wsConnectionClosed="wsConnectionClosed"
      />
      <JanusCasterCommunication
        v-if="makeLowrateVariant && enableStationChinaProxy && chinaWrtcServer"
        :streamToPlay="streamDocId + '_lowratecaster'"
        :onAir="isCasting"
        :localStream="localCameraStream"
        :videoBitrate="lowrateVideoBitrate"
        :audioBitrate="lowrateAudioBitrate"
        :wrtcServer="chinaWrtcServer"
        :videoCodec="videoCodec"
        :udpEnabled="useUdp"
        @wsConnectionClosed="wsConnectionClosed"
      />
    </template>
    <template v-else>
      <CasterCommunication
        v-if="selectedWrtcServer"
        :streamToPlay="streamDocId"
        :onAir="isCasting"
        :localStream="localCameraStream"
        :videoBitrate="requiredVideoBitrate"
        :audioBitrate="requiredAudioBitrate"
        :wrtcServer="selectedWrtcServer"
        :videoCodec="videoCodec"
        :udpEnabled="useUdp"
        @onNetworkStats="onUpdateNetworkStats"
        @wsConnectionClosed="wsConnectionClosed"
      />
      <CasterCommunication
        v-if="makeLowrateVariant && selectedWrtcServer"
        :streamToPlay="streamDocId + '_lowratecaster'"
        :onAir="isCasting"
        :localStream="localCameraStream"
        :videoBitrate="lowrateVideoBitrate"
        :audioBitrate="lowrateAudioBitrate"
        :wrtcServer="selectedWrtcServer"
        :videoCodec="videoCodec"
        :udpEnabled="useUdp"
        @wsConnectionClosed="wsConnectionClosed"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable max-lines */
import * as NetworkStatCalculator from '@/modules/status/classes/NetworkScoreCalculator'
import { VideoObjectNetworkStats } from '@/classes/VideoObjectNetworkStats'
import { UserNetworkStats } from '@/types/networkstats'
import { debounce } from 'lodash'
import CasterCommunication from '@/components/CasterCommunication'
import JanusCasterCommunication from '@/components/JanusCasterCommunication'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useCurrentCast, useCurrentInvitedCaster } from '@/modules/casts/compositions'
import { useCodecs } from '@/modules/usermedia/compositions'
import { Feature, hasFeature } from '@/featureFlags/featureFlags'
import env from '@/env'
import store from '@/store'
import { useAddLog } from '@/modules/logging/compositions'

const debounceGetDevices = debounce(() => store.dispatch.usermedia.getDevices(), 100)

const { currentCast } = useCurrentCast()
const { isRejected } = useCurrentInvitedCaster()
const { videoCodec } = useCodecs()
const { addLog } = useAddLog()

const statsLogCounter = ref(0)
const defaultVideoBitrate = ref(320)
const lowrateAudioBitrate = ref(32)

const enableBandwidthCheck = computed(() => !store.state.user.profile?.anonymous && !store.state.usermedia.isModerator)
const chinaWrtcServer = computed(() => store.state.usermedia.wrtcServerChina)
const enableStationChinaProxy = computed(() => store.state.usermedia.forceChinaProxy)
const isJanus = computed(() => hasFeature(Feature.JANUS) ?? false)
const selectedWrtcServer = computed(() => env.chinaBuild
  ? store.state.usermedia.wrtcServerChinaProxy
  : store.state.usermedia.wrtcServer)
const currentUserId = computed(() => store.state.user.id)
const localCameraStream = computed(() => store.state.usermedia.localCameraStream)
const lowrateVideoBitrate = computed(() => defaultVideoBitrate.value)
const userProfile = computed(() => store.state.user.profile)
const requiredAudioBitrate = computed(() => userProfile.value?.encoderSettings?.audioBitrate)
const requiredVideoBitrate = computed(() => userProfile.value?.encoderSettings?.videoBitrate)
const useUdp = computed(() => store.getters.user.useUdp)
const makeLowrateVariant = computed(() => requiredVideoBitrate.value !== undefined
  && requiredVideoBitrate.value >= (defaultVideoBitrate.value * 2))
const enableCasterCommunication = computed(() => !!streamDocId.value && !!localCameraStream.value
  && !!selectedWrtcServer.value && !isRejected.value)
const selectedAudioDevice = computed(() => userProfile.value?.selectedAudioDevice ?? null)
const selectedVideoDevice = computed(() => userProfile.value?.selectedVideoDevice ?? null)
const needRepublish = computed(() => store.state.user.needRepublish)

const streamDocId = computed({
  get: (): string|null => store.state.usermedia.streamDocId,
  set: (id: string|null) => store.dispatch.usermedia.setStreamDocId(id)
})

const isCasting = computed({
  get: (): boolean => store.state.events.casting,
  set: (isCasting: boolean) => store.dispatch.events.setCasting(isCasting)
})

watch([selectedAudioDevice, selectedVideoDevice], restartStream)
watch(needRepublish, async () => {
  if (!needRepublish.value) return
  await restartStream()
})

onMounted(async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    await addLog('Client browser does not support getUserMedia', 'error')
  }

  // Listen to MediaDevices API ondevicechange event
  // see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/ondevicechange
  navigator.mediaDevices.addEventListener('devicechange', debounceGetDevices)
})

onUnmounted(() => {
  navigator.mediaDevices.removeEventListener('devicechange', debounceGetDevices)
})

async function restartStream (): Promise<void> {
  if (!localCameraStream.value) return
  await store.dispatch.usermedia.restartStream()
}

async function wsConnectionClosed (): Promise<void> {
  await addLog('wsConnectionClosed', 'info')
}

/* eslint-disable-next-line max-lines-per-function, complexity */
async function onUpdateNetworkStats (stats: VideoObjectNetworkStats): Promise<void> {
  const profileStats: Partial<UserNetworkStats> = {
    upload: {
      jitter: stats.jitter ?? 0,
      latency: stats.latency ?? 0,
      score: stats.score ?? 0,
      bandwidth: stats.upload ?? 0,
      region: userProfile.value?.transcodingregion ?? 'unknown'
    },
    download: {
      bandwidth: stats.download ?? 0
    }
  }
  const myDownloadScore = store.getters.networkStats.myNetworkStats?.download
  if (myDownloadScore !== undefined && myDownloadScore.jitter === undefined) {
    if (stats.download !== undefined && profileStats.download !== undefined) {
      profileStats.download.score = NetworkStatCalculator.getScoreBasedOnBandwidth(stats.download)
    }
  }
  if (stats.encoder !== undefined) {
    profileStats.encoder = {
      adaptation: stats.encoder.adaptation ?? 'none',
      cpu_usage: stats.encoder.cpu_usage ?? 0,
      fps_in: stats.encoder.fps_in ?? 0,
      fps_out: stats.encoder.fps_out ?? 0,
      width: stats.encoder.width ?? 0,
      height: stats.encoder.height ?? 0,
      video_bitrate: stats.encoder.video_bitrate ?? 0,
      audio_bitrate: stats.encoder.audio_bitrate ?? 0
    }
  }
  await store.dispatch.user.updateStats(profileStats)

  const userId = userProfile.value?.assignedId ?? currentUserId.value
  const castId = currentCast.value?.id
  if (!userId || !castId) return
  await addLog('MetricReport: CasterNetwork', 'info', {
    cast: castId,
    jitter: stats.jitter,
    latency: stats.latency,
    k360event: currentCast.value?.k360_event_id ?? undefined,
    caster: userId,
    logger_name: 'metrics'
  })
  if (statsLogCounter.value % 5 === 0) {
    await addLog(`Bandwidth info of ${ userId } (score = ${ stats.score })`, 'info', {
      stats: JSON.parse(JSON.stringify(stats))
    })
  }
  statsLogCounter.value += 1
}

</script>
